<template>
	<section class="page">
		<PrimeMenubar class="toolbar-top" :model="menubarItems">
			<template #start>
				<RouterLink to="/">
					<img class="toolbar-logo" src="@/assets/goodhue-logo.svg" />
				</RouterLink>
			</template>
			<template #item="{ item, props, hasSubmenu }">
				<router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
					<a :href="href" v-bind="props.action" @click="navigate">
						<span :class="item.icon" />
						<span>{{ item.label }}</span>
					</a>
				</router-link>
				<a v-else :href="item.url" :target="item.target" v-bind="props.action">
					<span :class="item.icon" />
					<span>{{ item.label }}</span>
					<span v-if="hasSubmenu" class="pi pi-fw pi-angle-down" />
				</a>
			</template>
			<template #end>
				<div class="flex flex-row align-center" style="gap: 8px">
					<span style="color: var(--p-surface-400); text-wrap: nowrap">
						NSFW container status:
					</span>
					<PrimeTag
						v-if="nsfwContainerStatus === true"
						severity="success"
						value="Online"
					/>
					<PrimeTag
						v-else-if="nsfwContainerStatus === false"
						severity="danger"
						value="Offline"
					/>
					<PrimeSkeleton v-else style="width: 50px" />
					<PrimeDivider layout="vertical" />

					<PrimeSplitButton severity="secondary" size="small" :model="userButtonItems">
						<PrimeSkeleton v-if="!userEmail" style="width: 150px" />
						<span v-else style="color: var(--p-surface-400)">{{ userEmail }}</span>
					</PrimeSplitButton>
				</div>
			</template>
		</PrimeMenubar>
		<div class="toolbar-background"></div>

		<div class="content">
			<router-view />
		</div>

		<PrimeToast style="margin-top: 64px" />
		<PrimeScrollTop />
	</section>
</template>
<script lang="ts">
import { defineComponent, ref } from "vue";
import { computedAsync } from "@vueuse/core";

import PrimeMenubar from "primevue/menubar";
import PrimeSplitButton from "primevue/splitbutton";
import PrimeSkeleton from "primevue/skeleton";
import PrimeTag from "primevue/tag";
import PrimeDivider from "primevue/divider";
import PrimeScrollTop from "primevue/scrolltop";
import PrimeToast from "primevue/toast";

import { PrimeIcons } from "@primevue/core/api";

import { useToast } from "primevue/usetoast";
import { useRouter, useRoute } from "vue-router";

import { supabase } from "@/ts/supabase";
import { checkContainer } from "@/ts/nsfwChecker";

export default defineComponent({
	name: "PageView",

	components: {
		PrimeMenubar,
		PrimeSplitButton,
		PrimeSkeleton,
		PrimeTag,
		PrimeDivider,
		PrimeScrollTop,

		PrimeToast
	},

	setup() {
		const router = useRouter();
		const route = useRoute();

		const toast = useToast();
		const userEmail = computedAsync(async () => {
			const { data, error } = await supabase.auth.getUser();

			return error ? "..." : data.user?.email;
		}, undefined);

		const nsfwContainerStatusRetriggerKey = ref(0);
		const nsfwContainerStatus = computedAsync(async () => {
			nsfwContainerStatusRetriggerKey.value;
			return checkContainer();
		}, null);

		const menubarItems = ref([
			{
				label: "Statistics",
				icon: PrimeIcons.CHART_BAR,
				route: "/stats"
			},
			{
				label: "Gallery Preview",
				icon: PrimeIcons.IMAGES,
				route: "/gallery-preview"
			}
		]);
		const userButtonItems = ref([
			{
				label: "Logout",
				command: logout
			}
		]);

		//

		async function logout() {
			const { error } = await supabase.auth.signOut({ scope: "local" });

			if (!error) {
				await router.push("login");
			} else {
				toast.add({
					summary: "Unable to logout",
					detail: error.message,
					severity: "error"
				});
			}
		}

		//

		return {
			route,

			menubarItems,
			userButtonItems,

			userEmail,
			nsfwContainerStatus
		};
	}
});
</script>

<style lang="scss" scoped>
.page {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	.toolbar-top {
		flex-wrap: nowrap;
		position: sticky;
		z-index: 2;
		top: 16px;

		width: calc(100% - 32px);
		height: 64px;
		margin: 16px;

		box-shadow: 0px 0px 10px 5px var(--p-toolbar-background);

		.toolbar-logo {
			@media only screen and (max-width: 760px) {
				display: none;
			}

			@media (prefers-color-scheme: dark) {
				filter: invert(1);
			}
		}

		:deep(.p-menubar-end) {
			@media only screen and (max-width: 760px) {
				overflow-x: scroll;
			}
		}
	}

	.toolbar-background {
		position: fixed;
		z-index: 1;
		top: 0px;
		width: 100%;
		height: calc(64px + 16px);

		background: rgba(0, 0, 0, 0);
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
		backdrop-filter: blur(5px);
	}

	> .content {
		width: 100%;
		padding: 16px;
	}
}
</style>
